import { forwardRef, useImperativeHandle, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DatePicker, TextareaInput } from 'components/FormControl';

import { yupResolver } from '@hookform/resolvers/yup';
import { TOAST } from 'constant';
import { academicHistorySchema } from 'features/ScientificProfile/validation';
import { toast } from 'layout';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

const formOptions = { resolver: yupResolver(academicHistorySchema) };

const AcademicHistoryDialog = forwardRef((props, ref) => {
  const { t } = useTranslation();

  const [academicHistoryControl, setAcademicHistoryControl] = useState();
  const [visible, setVisible] = useState(false);

  const {
    control,
    watch,
    setValue,
    reset,
    trigger,
    formState: { errors, dirtyFields },
  } = useForm({ ...formOptions, mode: 'onChange' });
  // #region Event

  useImperativeHandle(
    ref,
    () => ({
      open: (_academicHistoryControl) => {
        setAcademicHistoryControl(_academicHistoryControl);
        setVisible(true);
        if (_academicHistoryControl.type === 'APPEND') {
          reset({});
        } else if (_academicHistoryControl.type === 'UPDATE') {
          reset(_academicHistoryControl.value);
        }
      },
    }),
    []
  );

  const handleAddAcademicHistory = async () => {
    setValue('academicDegree', academicHistoryControl.academicDegree);

    const isValidTrigger = await trigger();
    if (!isValidTrigger) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
      return;
    }

    const data = watch();

    const { append, update, index, type } = academicHistoryControl;

    if (type === 'APPEND' && append) {
      append(data);
    } else if (type === 'UPDATE' && update) {
      update(index, data);
    }
    reset({});
    setVisible(false);
  };

  // #endregion Event

  return (
    <Dialog
      header={
        academicHistoryControl?.type === 'APPEND'
          ? t('scientificProfile.academicHistory.add')
          : t('scientificProfile.academicHistory.update')
      }
      visible={visible}
      onHide={() => {
        setVisible(false);
      }}
      draggable={false}
      className="w-full sm:w-7 lg:w-6"
    >
      <div className="grid p-fluid">
        <div className="col-6">
          <DatePicker
            label={t('scientificProfile.academicHistory.start')}
            name="start"
            control={control}
            errors={errors}
            isRequired
          />
        </div>
        <div className="col-6">
          <DatePicker
            label={t('scientificProfile.academicHistory.end')}
            name="end"
            control={control}
            errors={errors}
            isRequired
          />
        </div>
        <div className="col-12">
          <TextareaInput
            label={t('scientificProfile.academicHistory.address')}
            name="address"
            control={control}
            errors={errors}
            isRequired
          />
        </div>

        <div className="col-12">
          <TextareaInput
            label={t('scientificProfile.academicHistory.major')}
            name="major"
            control={control}
            errors={errors}
            isRequired
          />
        </div>

        <div className="col-12">
          <TextareaInput
            label={t('scientificProfile.academicHistory.graduationThesisName')}
            name="thesisName"
            control={control}
            errors={errors}
          />
        </div>
      </div>

      <div className="flex flex-wrap justify-content-end mt-4">
        <Button
          label={t('formLayout.action.cancel')}
          severity="danger"
          onClick={() => setVisible(false)}
          className="mr-1 w-8rem"
        />
        <Button
          label={
            academicHistoryControl?.type === 'APPEND'
              ? t('formLayout.action.add')
              : t('formLayout.action.update')
          }
          severity="info"
          type="submit"
          className="w-8rem"
          onClick={handleAddAcademicHistory}
          disabled={!Object.keys(dirtyFields).length}
        />
      </div>
    </Dialog>
  );
});

AcademicHistoryDialog.propTypes = {};

AcademicHistoryDialog.defaultProps = {};

export default AcademicHistoryDialog;
